import {
    type ChangeEventHandler,
    createContext,
    type FocusEventHandler,
    type KeyboardEventHandler,
    type MouseEventHandler,
    type PointerEventHandler,
    type ReactNode,
    use,
} from "react";

import type { ChatInputLabels } from "../labels";

type ChatInputContextValue = {
    acceptedFileTypes?: readonly string[];
    activate: () => void;
    cancelRecording: () => void;
    canSend: boolean;
    elapsedMs: number;
    fieldEpoch: number;
    handleBlur: FocusEventHandler<HTMLTextAreaElement>;
    handleChange: ChangeEventHandler<HTMLTextAreaElement>;
    handleChatInputPress: MouseEventHandler<HTMLElement>;
    handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement>;
    handleTextareaPointerDown: PointerEventHandler<HTMLTextAreaElement>;
    input: string;
    isActive: boolean;
    isExpandable: boolean;
    isExpanded: boolean;
    isRecording: boolean;
    labels: ChatInputLabels;
    onFileSelected?: (file: File) => void;
    onSendVoice?: (file: File) => void;
    sendMessage: () => void;
    sendRecording: () => void;
    startRecording: () => void;
    toggleExpanded: () => void;
};

const ChatInputContext = createContext<ChatInputContextValue | null>(null);

type ChatInputProviderProps = {
    children: ReactNode;
    value: ChatInputContextValue;
};

export function ChatInputProvider({ children, value }: ChatInputProviderProps) {
    return <ChatInputContext value={value}>{children}</ChatInputContext>;
}

export function useChatInputContext() {
    const context = use(ChatInputContext);

    if (context === null) {
        throw new Error(
            "Chat input components must be rendered within ChatInputProvider.",
        );
    }

    return context;
}
